import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
const ChatBot = React.lazy(() => import("./components/ChatBot"));
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";

// Pages
// Pages publiques SEO — imports statiques (légères, sans framer-motion)
import SechoirSolairePage from "./pages/SechoirSolairePage";
import PanneauPvtAirPage from "./pages/PanneauPvtAirPage";
import InstallateursPage from "./pages/InstallateursPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "./pages/PolitiqueConfidentialitePage";
import FicheCeeAgriEq110Page from "./pages/FicheCeeAgriEq110Page";
import RealisationsPage from "./pages/RealisationsPage";
import BatimentSechoirOffertPage from "./pages/BatimentSechoirOffertPage";
// Pages publiques avec animations framer-motion — chargées en différé pour
// que la librairie ne pèse pas sur les pages SEO (objectif Lighthouse 80+).
const HomePage = React.lazy(() => import("./pages/HomePage"));
const BureauEtudesPage = React.lazy(() => import("./pages/BureauEtudesPage"));
const FinancementPage = React.lazy(() => import("./pages/FinancementPage"));
const ChauffagePage = React.lazy(() => import("./pages/ChauffagePage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const FindInstallerPage = React.lazy(() => import("./pages/FindInstallerPage"));
const CalculatorPage = React.lazy(() => import("./pages/CalculatorPage"));
const DocumentationSechoir = React.lazy(() => import("./pages/DocumentationSechoir"));

// Pages internes lourdes — chargées à la demande (code-splitting) pour
// alléger le bundle public (SEO / Lighthouse).
const VTApp = React.lazy(() => import("./VTApp"));
const EtudeVT = React.lazy(() => import("./pages/EtudeVT"));
const Calculette = React.lazy(() => import("./pages/Calculette"));
const DashboardVT = React.lazy(() => import("./pages/DashboardVT"));
const ScenarioThermasol = React.lazy(() => import("./pages/ScenarioThermasol"));
const CRM = React.lazy(() => import("./pages/CRM"));
const DevisV2 = React.lazy(() => import("./pages/DevisV2"));
const DevisHVAC = React.lazy(() => import("./pages/DevisHVAC"));
const DevisV3PVTTest = React.lazy(() => import("./pages/DevisV3PVTTest"));
const DimensionnementV4Page = React.lazy(() => import("./pages/DimensionnementV4Page"));
const AdminEngineDebug = React.lazy(() => import("./pages/AdminEngineDebug"));
const CanonicalAnomaliesDashboard = React.lazy(() => import("./components/be_governance/CanonicalAnomaliesDashboard"));
const FanQualificationDashboard = React.lazy(() => import("./components/be_governance/FanQualificationDashboard"));
const AdminReportPreview = React.lazy(() => import("./pages/AdminReportPreview"));
const ProjetWorkflow = React.lazy(() => import("./pages/ProjetWorkflow"));
const SimulationClient = React.lazy(() => import("./pages/SimulationClient"));
const DashboardClients = React.lazy(() => import("./pages/DashboardClients"));
const ExploitationsLibrary = React.lazy(() => import("./pages/ExploitationsLibrary"));
const QuotesLibrary = React.lazy(() => import("./pages/QuotesLibrary"));
const PdfPreview = React.lazy(() => import("./pages/PdfPreview"));
const ClientComparatif = React.lazy(() => import("./pages/ClientComparatif"));
const VerifyPage = React.lazy(() => import("./pages/VerifyPage"));
const VerifyRefPage = React.lazy(() => import("./pages/VerifyRefPage"));
const PdfLibrary = React.lazy(() => import("./pages/PdfLibrary"));
const FirewoodStudio = React.lazy(() => import("./pages/FirewoodStudio"));
const AdminCEEConfig = React.lazy(() => import("./pages/AdminCEEConfig"));
const AdminBeFanCatalog = React.lazy(() => import("./pages/AdminBeFanCatalog"));
const AdminBeInverterCatalog = React.lazy(() => import("./pages/AdminBeInverterCatalog"));
const AdminElectricalDemo = React.lazy(() => import("./pages/AdminElectricalDemo"));
const AdminVariantesRenforcement = React.lazy(() => import("./pages/AdminVariantesRenforcement"));
const AdminCompletudeCatalogue = React.lazy(() => import("./pages/AdminCompletudeCatalogue"));
const BeRoofGeometryPreviewPage = React.lazy(() => import("./pages/BeRoofGeometryPreviewPage"));
import RequireCrmAuth from "./components/RequireCrmAuth";

// Vague 17 — Système Debug Global
import { DebugProvider } from "./components/debug/DebugProvider";
import { applySeo } from "./components/Seo";

// SEO par défaut : appliqué à chaque navigation, avant le Seo éventuel de la page (ordre des effets)
function DefaultSeo({ noindex }) {
  const location = useLocation();
  React.useEffect(() => {
    applySeo({
      title: "Thermasol Air Systems® | Séchage & Chauffage Solaire | Solutions Thermovoltaïques",
      description: "Thermasol Air Systems® : séchage solaire et chauffage par aérothermie. Technologie PVT hybride déposée INPI pour agriculture et industrie. Réduisez vos coûts de 50 à 90%.",
      ogImage: "https://thermasol-airsystems.fr/images/seo/hero.webp",
      noindex,
    });
  }, [location.pathname, noindex]);
  return null;
}

// Layout interne : masque Navbar/Footer/ChatBot/CookieBanner si la page est chargée
// en mode "embed" (ex : Devis V2 intégré dans l'iframe du CRM via ?embed=1)
function AppLayout() {
  const location = useLocation();
  const isEmbed = new URLSearchParams(location.search).get("embed") === "1";
  // L'écran admin THERMASOL ENGINE Debug a sa propre chrome (sombre, fullscreen)
  // — on masque Navbar / Footer / ChatBot / CookieBanner pour éviter les overlays.
  const isAdminTool = location.pathname.startsWith("/admin/");
  // Phase 8 — Les routes /pdf-preview et /verify sont "print-only"
  const isPdfRoute = location.pathname.startsWith("/pdf-preview/") || location.pathname.startsWith("/verify") || location.pathname.startsWith("/client-comparatif");
  const hideShell = isEmbed || isAdminTool || isPdfRoute;
  // SEO : les outils internes ne doivent jamais être indexés par Google
  const isInternal = isAdminTool || isPdfRoute || ["/devis-v2", "/devis-hvac", "/dashboard-vt", "/be/", "/dimensionnement-v4", "/devis-v3-pvt-test", "/visite-technique"].some((p) => location.pathname.startsWith(p));
  return (
    <>
      <DefaultSeo noindex={isInternal} />
      <ScrollToTop />
      {!hideShell && <Navbar />}
      <React.Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/sechoir-solaire" element={<SechoirSolairePage />} />
        <Route path="/batiment-sechoir-offert" element={<BatimentSechoirOffertPage />} />
        <Route path="/panneau-pvt-air" element={<PanneauPvtAirPage />} />
        <Route path="/installateurs" element={<InstallateursPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
        <Route path="/fiche-cee-agri-eq-110" element={<FicheCeeAgriEq110Page />} />
        <Route path="/realisations" element={<RealisationsPage />} />
        <Route path="/bureau-etudes" element={<BureauEtudesPage />} />
        <Route path="/sechoir" element={<Navigate to="/sechoir-solaire" replace />} />
        <Route path="/chauffage" element={<ChauffagePage />} />
        <Route path="/financement" element={<FinancementPage />} />
        <Route path="/visite-technique" element={<VTApp />} />
        <Route path="/etude" element={<EtudeVT />} />
        <Route path="/calculette" element={<Navigate to="/" replace />} />
        <Route path="/dashboard-vt" element={<DashboardVT />} />
        <Route path="/documentation-sechoir" element={<DocumentationSechoir />} />
        <Route path="/scenario-thermasol" element={<Navigate to="/" replace />} />
        <Route path="/crm" element={<Navigate to="/" replace />} />
        <Route path="/devis-v2" element={<DevisV2 />} />
        <Route path="/devis-hvac/:leadId" element={<DevisHVAC />} />
        <Route path="/devis-v3-pvt-test" element={<DevisV3PVTTest />} />
        <Route path="/dimensionnement-v4" element={<DimensionnementV4Page />} />
        <Route path="/admin/engine-debug" element={<AdminEngineDebug />} />
        <Route path="/be/canonical-anomalies" element={<CanonicalAnomaliesDashboard />} />
        <Route path="/be/fan-qualification" element={<FanQualificationDashboard />} />
        <Route path="/be/roof-geometry-preview" element={<BeRoofGeometryPreviewPage />} />
        <Route path="/admin/variantes-renforcement" element={<AdminVariantesRenforcement />} />
        <Route path="/admin/completude-catalogue" element={<AdminCompletudeCatalogue />} />
        <Route path="/admin/report-preview" element={<AdminReportPreview />} />
        <Route path="/admin/document-preview" element={<RequireCrmAuth><ProjetWorkflow /></RequireCrmAuth>} />
        <Route path="/admin/projet-workflow" element={<RequireCrmAuth><ProjetWorkflow /></RequireCrmAuth>} />
        <Route path="/admin/simulation-client" element={<SimulationClient />} />
        <Route path="/admin/dashboard-clients" element={<DashboardClients />} />
        <Route path="/admin/exploitations" element={<ExploitationsLibrary />} />
        <Route path="/admin/quotes" element={<QuotesLibrary />} />
        <Route path="/admin/pdf-library" element={<PdfLibrary />} />
        <Route path="/admin/firewood-studio" element={<FirewoodStudio />} />
        <Route path="/admin/cee-config" element={<AdminCEEConfig />} />
        <Route path="/admin/erp-catalogue" element={<Navigate to="/" replace />} />
        <Route path="/admin/be-fan-catalog" element={<AdminBeFanCatalog />} />
        <Route path="/admin/be-inverter-catalog" element={<AdminBeInverterCatalog />} />
        <Route path="/admin/electrical-demo" element={<AdminElectricalDemo />} />
        {/* Phase 8 — PDF Premium BE */}
        <Route path="/pdf-preview/:reference" element={<PdfPreview />} />
        <Route path="/client-comparatif" element={<ClientComparatif />} />
        <Route path="/verify/:hash" element={<VerifyPage />} />
        <Route path="/verify-ref/:reference" element={<VerifyRefPage />} />
        <Route path="/find-installer" element={<FindInstallerPage />} />
        <Route path="/calculator" element={<CalculatorPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
      </React.Suspense>
      {!hideShell && <Footer />}
      {!hideShell && <React.Suspense fallback={null}><ChatBot /></React.Suspense>}
      {!hideShell && <CookieBanner />}
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <DebugProvider>
        <div className="App" data-testid="app-container">
          <BrowserRouter>
            <AppLayout />
          </BrowserRouter>
        </div>
      </DebugProvider>
    </LanguageProvider>
  );
}

export default App;
